import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Paper,
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Slide,
} from "@material-ui/core";

import { IKImage } from "imagekitio-react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Add, Remove, Cancel, Link, KeyboardArrowDown } from "@material-ui/icons";
import { useSwipeable } from "react-swipeable";
import { PFXCardType, PFXSubItemChosenOption, PFXSubItemData, PFXSubitemDataType } from "../../../models/Interfaces";
import "moment/locale/es";
import { useMarketingContext, Events } from "../../../contexts/MarketingContext";
import { useSelector } from "react-redux";
import {
  selectCardType,
  selectSkusEntities,
  selectCurrency,
  selectItemToShow,
  selectItemById,
} from "../../../redux/slices/templateSlice";
import useTemplateStyles from "../../../hooks/useTemplateStyles";
import { currencyFormatter, selectCleverlynk, selectUnavailable } from "../../../redux/slices/cleverlynkSlice";
import useCleverlynkFunctions from "../../../hooks/useCleverlynkFunctions";
import useTemplateFunctions from "../../../hooks/useTemplateFunctions";
import { cloneDeep, debounce } from "lodash";
import { TransitionProps } from "@material-ui/core/transitions";
import CarouselArrow from "./components/CarouselArrows";
import CarouselDots from "./components/CarouselDots";
import MultipleOption from "./components/MultipleOption";
import SubitemCheckbox from "./components/SubitemCheckbox";
import SubitemDate from "./components/SubitemDate";
import SubitemRadio from "./components/SubitemRadio";
import SubitemTextField from "./components/SubitemTextField";
import UpsellingSection from "./components/UpsellingSection";
import useDisableOverflow from "../../../hooks/useDisableOverflow";
import { RootState } from "../../../redux/rootReducer";

import { useTranslation } from "react-i18next";
import "../../../i18n/config";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const RIGHT = 1;
export const LEFT = -1;

export interface IItemCardModal {
  setClose: () => void;
}

const ItemCardModal = ({ setClose }: IItemCardModal) => {
  const item = useSelector(selectItemToShow);
  const upsellingItem = useSelector((state: RootState) => selectItemById(state, item?.upsellingId ?? ""));

  const [newQuantity, setNewQuantity] = useState(1);
  const [selectedSubitems, setSelectedSubitems] = useState<PFXSubItemChosenOption[]>([]);
  const [image, setImage] = useState<any>(undefined);
  const [imageWidth, setImageWidth] = useState<number | undefined>(undefined);
  const [focusIndex, setFocusIndex] = useState<number>(0);

  const skus = useSelector(selectSkusEntities);
  const currency = useSelector(selectCurrency);
  const cleverlynk = useSelector(selectCleverlynk);
  const unavailable = useSelector(selectUnavailable);
  const marketingContext = useMarketingContext();
  const cardType = useSelector(selectCardType);

  const { t } = useTranslation("common");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { handleModifyIndividualSku, handleAddRecentlyViewed } = useTemplateFunctions();
  const { titleStyles, cardStyles } = useTemplateStyles();
  const { addItemToCart, handleModifySnackbar } = useCleverlynkFunctions();
  const { disable, enable } = useDisableOverflow();

  const toggleScrollArrow = React.useRef(
    debounce((isMobile, iWidth) => {
      const mobileElem = document.getElementById(`scroller-div-item`);

      const infoElem = document.getElementById("item-card-info-area");
      const dialogContentElem = document.getElementById("item-card-dialog-content");
      const arrowElem = document.getElementById(`scroll-arrow`);
      if (arrowElem && mobileElem && isMobile && dialogContentElem) {
        if (mobileElem.scrollHeight > window.innerHeight) {
          const remainingScroll = mobileElem.scrollHeight - window.innerHeight - dialogContentElem.scrollTop;
          if (remainingScroll < 50) {
            arrowElem.style.color = "#00000000";
          } else {
            arrowElem.style.color = titleStyles?.color ?? "white";
          }
        } else {
          arrowElem.style.color = "#00000000";
        }
      } else if (infoElem && arrowElem && mobileElem && dialogContentElem) {
        const elem = document.getElementsByClassName("subitem-render-div");
        if (elem) {
          let subitemsHeight = 100;
          for (let index = 0; index < elem.length; index++) {
            subitemsHeight += elem[index]?.scrollHeight ?? 0;
          }
          const remainingScroll = subitemsHeight - mobileElem?.scrollHeight - infoElem.scrollTop ?? 0;
          if (remainingScroll > 50) {
            arrowElem.style.color = titleStyles?.color ?? "white";
          } else {
            arrowElem.style.color = "#00000000";
          }
        }
      }
    }, 20)
  ).current;

  const open = useMemo(() => !!item, [item]);

  const actualPrice = React.useMemo(() => {
    return (
      newQuantity *
      ((item?.priceWithDiscount ?? 0) +
        selectedSubitems.reduce((acc, val) => acc + (val.value || 0) * (val.quantity || 0), 0))
    );
  }, [selectedSubitems, newQuantity, item]);

  useEffect(() => {
    if (open) {
      disable();
    } else enable();
    setTimeout(() => {
      const mobileElem = document.getElementById(`scroller-div-item`);

      const infoElem = document.getElementById("item-card-info-area");
      const dialogContentElem = document.getElementById("item-card-dialog-content");
      const arrowElem = document.getElementById(`scroll-arrow`);
      if (arrowElem && mobileElem && isMobile && dialogContentElem) {
        if (mobileElem.scrollHeight > window.innerHeight) {
          const remainingScroll = mobileElem.scrollHeight - window.innerHeight - dialogContentElem.scrollTop;
          if (remainingScroll < 50) {
            arrowElem.style.color = "#00000000";
          } else {
            arrowElem.style.color = titleStyles?.color ?? "white";
          }
        } else {
          arrowElem.style.color = "#00000000";
        }
      } else if (infoElem && arrowElem && dialogContentElem && mobileElem) {
        const elem = document.getElementsByClassName("subitem-render-div");
        if (elem) {
          let subitemsHeight = 100;
          for (let index = 0; index < elem.length; index++) {
            subitemsHeight += elem[index]?.scrollHeight ?? 0;
          }
          if (subitemsHeight - mobileElem?.scrollHeight > 50) {
            arrowElem.style.color = titleStyles?.color ?? "white";
          } else {
            arrowElem.style.color = "#00000000";
          }
        }
      }
    }, 50);
  }, [open]);

  const handleClose = () => {
    setClose();
    const elem = document.getElementsByTagName("body");
    if (elem && elem[0]) {
      elem[0].style.overflowY = "auto";
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedSubitems([]);
      setNewQuantity(1);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (
        (marketingContext?.pixelId || marketingContext?.googleAnalyticsId || marketingContext?.newGoogleAnalyticsId) &&
        marketingContext.loadedMarketingScripts
      ) {
        marketingContext?.trackEvents(
          Events.VIEW_CONTENT,
          {
            content_name: item?.name,
            value: item?.price,
            currency: "COP",
          },
          {
            event_category: "View content",
            event_label: `${item?.name}`,
            value: item?.price || 0,
          }
        );
      }
    }
  }, [open, item, marketingContext]);

  useEffect(() => {
    if (imageWidth && item) {
      let img = (item.images ?? [])[(item?.images ?? []).filter(i => i !== "").length === 1 ? 0 : focusIndex];
      if (img) {
        img = img.split("cleverlynk-imgs.s3.amazonaws.com")[1]?.replace("resized-", "");
      } else {
        img = "no_image";
      }

      setImage(
        <IKImage
          key={imageWidth + img}
          style={{
            height: cardType === PFXCardType.VERTICAL ? "100%" : imageWidth,
            width: cardType === PFXCardType.VERTICAL ? "100%" : "",
            objectFit: cardType === PFXCardType.VERTICAL ? "cover" : "",
            maxWidth: cardType === PFXCardType.VERTICAL ? "" : "100%",
            position: "relative",
            zIndex: 2,
          }}
          path={img}
          transformation={[
            {
              height: Math.max(1150, imageWidth),
              width: Math.max(1150, imageWidth),
            },
          ]}
          loading="lazy"
          lqip={{ active: true, quality: 10, blur: 40 }}
        />
      );
    }
  }, [cardType, focusIndex, imageWidth, item]);

  const useStyles = makeStyles({
    root: {
      width: "calc(100% - 16px)",
      maxHeight: "calc(100% - 16px)",
      margin: 0,
    },
    media: {
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      MozBackgroundSize: "cover",
      WebkitBackgroundSize: "cover",
      OBackgroundSize: "cover",
      width: "100%",
      borderRadius: "0px",
      "&.vertical": {
        height: upsellingItem && !upsellingItem.soldOut ? "calc(100% - 170px)" : "100%",
        display: "flex",
      },
    },
    detail: {
      padding: isMobile ? 4 : theme.spacing(2),
      paddingBottom: isMobile ? 2 : theme.spacing(1),
      height: isMobile ? "" : "100%",
      position: isMobile ? "static" : "relative",
    },
    cardBackground: {
      backgroundColor: cardStyles?.cardColor,
      // overflowX: "hidden",
    },
    text: {
      "&.title": {
        textAlign: "left",
        fontSize: "1.5rem",
        fontWeight: "bold",
      },
      "&.description": {
        fontFamily: titleStyles?.textFontFamily ?? "Inter",
      },
    },
    info: {
      maxHeight: "100%",
      overflowY: "auto",
      padding: 5,
      paddingBottom: 75,
      alignContent: "baseline",
    },
    dialogFullWidth: {
      overflow: "hidden",
      width: "100%",
      margin: 0,
      maxHeight: "100%",
      maxWidth: isMobile ? 650 : "",
      minHeight: isMobile ? "65%" : undefined,
    },
    dialogContent: {
      padding: "0 !important",
    },
    addbuttons: {
      backgroundColor: `${cardStyles?.cardColor} !important`,
      marginTop: 10,
      marginLeft: isMobile ? "" : -10,
      position: "absolute",
      bottom: 0,
      paddingBottom: 8,
      paddingTop: 4,
    },
    priceName: {
      padding: isMobile ? "0px 2vw" : 0,
    },
    cancelIcon: {
      position: "absolute",
      right: "0",
      top: "0",
      margin: 6,
      zIndex: 999,
      padding: 3,
    },
    iconButtons: {
      background: "none !important",
      color: "black !important",
    },
    linkButtonRoot: {
      backgroundColor: "whitesmoke",
      borderRadius: "3pc",
      transitionDuration: "400ms",
      transitionProperty: "background-color",
      "&:hover": {
        backgroundColor: "#808080",
      },
    },
    "@keyframes scrollArrowAnimation": {
      "0%": {
        bottom: 12,
      },
      "25%": {
        bottom: -6,
      },
      "100%": {
        bottom: 12,
      },
    },
    scrollArrow: {
      position: "absolute",
      animation: "$scrollArrowAnimation 1500ms infinite",
      color: titleStyles?.color ?? "white",
      transition: "color 0.4s",
    },
    scrollArrowDiv: {
      marginTop: 15,
    },
  });

  const classes = useStyles();

  const noImage = React.useMemo(() => {
    return Boolean((item?.images ?? []).every(i => i.trim() === ""));
  }, [item]);

  const verifySubitems = React.useMemo((): { passes: boolean; missing: string[] } => {
    let passes = true;
    let missing: string[] = [];
    const textAndDateSubitems = selectedSubitems.filter(s => s.subitemType);
    const optionSubitems = selectedSubitems.filter(s => !s.subitemType);
    if (item?.subitems) {
      item?.subitems
        .filter(sub => sub.mandatory && !sub.hidden)
        .forEach(sub => {
          if (!sub.type) {
            const foundSelectedSubitem = optionSubitems.find(s => s.subitemId === sub.id);
            if (!foundSelectedSubitem) {
              missing.push(sub.name!);
              passes = false;
            } else if (sub.choiceMinimum) {
              const chosenAmount = optionSubitems.reduce(
                (prev, acc) => (acc.subitemId === sub.id ? acc.quantity ?? 0 : 0) + prev,
                0
              );
              if (chosenAmount < sub.choiceMinimum) {
                missing.push(`${sub.choiceMinimum - chosenAmount} opciones de ${sub.name!}`);
                passes = false;
              }
            }
            selectedSubitems
              .filter(s => Boolean(s.subitemType))
              .forEach(s => {
                return s.input && s.input.trim() !== "";
              });
          } else {
            if (!textAndDateSubitems.find(s => s.subitemId === sub.id && s.input?.trim() !== "")) {
              missing.push(sub.name!);
              passes = false;
            }
          }
        });
    }
    return { passes, missing };
  }, [item?.subitems, selectedSubitems]);

  const decreaseSubitemsStock = () => {
    if (verifySubitems.passes) {
      selectedSubitems
        .filter(ss => ss.controlsInventory)
        .forEach(sci => {
          if (skus[sci.sku!]) {
            let newValue = skus[sci.sku!]!.stock ?? 0;
            newValue = newValue - sci.quantity!;
            handleModifyIndividualSku(sci.sku!, newValue);
          }
        });
    }
  };

  const photoNavClick = (new_index, direction) => {
    let maxLength = (item?.images?.length ?? 0) - 1;
    if (new_index === -1 && direction === LEFT) {
      photoNavClick(maxLength, direction);
    } else if (new_index === maxLength + 1 && direction === RIGHT) {
      photoNavClick(0, direction);
    } else if ((item?.images ?? [])[new_index] === "") {
      photoNavClick(new_index + direction, direction);
    } else setFocusIndex(new_index);
  };

  const getChosen = (id: string, unique?: boolean) => {
    if (unique) return selectedSubitems.find(s => s.subitemId === id);
    return selectedSubitems.filter(s => s.subitemId === id);
  };

  const handleSubitemChange = (options: PFXSubItemChosenOption[], id: string) => {
    setSelectedSubitems([...selectedSubitems].filter(s => s.subitemId !== id).concat(options));
  };

  const handleSubitemDelete = id => {
    setSelectedSubitems([...selectedSubitems].filter(s => s.subitemId !== id));
  };

  const renderSubitem = (subi: PFXSubItemData) => {
    let sub = cloneDeep(subi);
    sub.choiceLimit = sub.choiceLimit === null ? undefined : sub.choiceLimit;
    sub.choiceMinimum = sub.choiceMinimum === null ? undefined : sub.choiceMinimum;

    let subitemSkuMap = new Map<string, number>();
    if (sub.controlsInventory) {
      sub.options = sub.options!.map(o => {
        if (o.sku && o.sku !== "") {
          let stck = skus[o.sku]?.stock;
          if (stck && stck > 0) subitemSkuMap.set(o.sku, stck);
          else subitemSkuMap.set(o.sku, 0);
        }
        return o;
      });
    }
    if (sub.type === "DATE") {
      return (
        <SubitemDate
          setChosen={(input: string, id: string) =>
            handleSubitemChange(
              [
                {
                  id,
                  controlsInventory: false,
                  hidden: false,
                  input,
                  label: sub.name,
                  quantity: 0,
                  sku: "",
                  subitemId: sub.id,
                  subitemType: PFXSubitemDataType.DATE,
                  value: 0,
                } as PFXSubItemChosenOption,
              ],
              sub.id!
            )
          }
          key={"subitemdate" + sub.id}
          subitem={sub}
          textFontFamily={titleStyles?.textFontFamily ?? "Inter"}
        />
      );
    } else if (sub.type === "TEXTFIELD") {
      return (
        <SubitemTextField
          key={"subitemtext-" + sub.id!}
          subitem={sub}
          setChosen={(input: string, id: string) =>
            handleSubitemChange(
              [
                {
                  id,
                  controlsInventory: false,
                  hidden: false,
                  input,
                  label: sub.name,
                  quantity: 0,
                  sku: "",
                  subitemId: sub.id,
                  subitemType: PFXSubitemDataType.TEXTFIELD,
                  value: 0,
                } as PFXSubItemChosenOption,
              ],
              sub.id!
            )
          }
          textFontFamily={titleStyles?.textFontFamily ?? "Inter"}
        />
      );
    } else if (sub.multiple) {
      return (
        <MultipleOption
          skuMap={subitemSkuMap}
          key={"subitemmultiple" + sub.id}
          sub={sub}
          chosen={getChosen(sub.id!) as PFXSubItemChosenOption[]}
          setChosen={(options, id) => handleSubitemChange(options, id)}
          textFontFamily={titleStyles?.textFontFamily ?? "Inter"}
        />
      );
    } else {
      if (sub.choiceLimit === 1)
        return (
          <SubitemRadio
            key={"subitemradio" + sub.id}
            subitem={sub}
            chosen={getChosen(sub.id!, true) as PFXSubItemChosenOption | undefined}
            setChosen={(option, id) => handleSubitemChange([option], id)}
            textFontFamily={titleStyles?.textFontFamily ?? "Inter"}
            onDelete={() => handleSubitemDelete(sub.id)}
            skuMap={subitemSkuMap}
          />
        );
      else
        return (
          <SubitemCheckbox
            key={"subitemcheckbox" + sub.id}
            subitem={sub}
            limit={sub.choiceLimit!}
            chosen={getChosen(sub.id!) as PFXSubItemChosenOption[]}
            setChosen={(options, id) => handleSubitemChange(options, id)}
            textFontFamily={titleStyles?.textFontFamily ?? "Inter"}
            skuMap={subitemSkuMap}
          />
        );
    }
  };

  const submitCallback = item => {
    if (item.sku) {
      let newValue = skus[item.sku!]!.stock ?? 0;
      newValue = newValue - newQuantity;
      handleModifyIndividualSku(item.sku!, newValue);
    }
    handleClose();
  };

  const submit = () => {
    decreaseSubitemsStock();
    if (!verifySubitems.passes) {
      handleModifySnackbar({
        type: "warning",
        message: `${t("youMustChoose")} ${verifySubitems.missing.join(", ")}`,
      });
    } else {
      addItemToCart(newQuantity, selectedSubitems, item!, () => submitCallback(item!));
      marketingContext?.trackEvents(
        Events.ADD_TO_CART,
        {
          content_name: item?.name,
          value: item?.price! * newQuantity,
          currency: "COP",
        },
        {
          event_category: "Add to cart",
          event_label: `${item?.name}`,
          value: item?.price ? item?.price! * newQuantity : 0,
        }
      );
    }
  };

  const shouldCenter = React.useMemo(() => {
    return (
      item?.decorative ||
      ((!item?.description || item?.description === "" || item?.description.split("").length < 150) &&
        (!item?.subitems || item?.subitems.filter(s => !s.hidden).length === 0))
    );
  }, [item]);

  const increaseQuantity = () => {
    if (!item?.sku || (skus[item?.sku!]?.stock ?? 0) > newQuantity) {
      setNewQuantity(newQuantity + 1);
    } else if (item?.maxAmountPerInstance && item?.maxAmountPerInstance < newQuantity) {
      handleModifySnackbar({
        type: "warning",
        message: t("unableToAddMore"),
      });
    } else {
      handleModifySnackbar({
        type: "warning",
        message: t("noAvailableUnits"),
      });
    }
  };

  const disableAddButton = React.useMemo(() => {
    return unavailable?.closed ? true : newQuantity < 1;
  }, [unavailable, newQuantity]);

  const swipeable = useSwipeable({
    onSwipedRight: () => photoNavClick(focusIndex + LEFT, LEFT),
    onSwipedLeft: () => photoNavClick(focusIndex + RIGHT, RIGHT),
  });

  const arrowScroll = () => {
    const desktop_elem = document.getElementById("item-card-info-area");
    const mobile_elem = document.getElementById("item-card-dialog-content");
    if (isMobile && mobile_elem) {
      mobile_elem.scrollTo({
        top: 400,
        behavior: "smooth",
      });
    } else if (!isMobile && desktop_elem) {
      desktop_elem.scrollTo({
        top: 400,
        behavior: "smooth",
      });
    }
  };

  const additionButtons = useMemo(() => {
    return !item?.decorative ? (
      <Grid
        container
        item
        xs={12}
        spacing={1}
        justifyContent={item?.maxAmountPerInstance === 1 ? "flex-end" : "center"}
        alignItems="center"
        className={`${classes.cardBackground} ${classes.addbuttons}`}>
        {(!item?.maxAmountPerInstance || item?.maxAmountPerInstance > 1) && (
          <Grid item container spacing={0} justifyContent="center" alignItems="center" xs={5}>
            <Paper
              style={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                backgroundColor: "whitesmoke",
                width: "70%",
              }}>
              <Grid container style={{ width: "100%" }}>
                <Grid item xs={4} container justifyContent="center">
                  <IconButton
                    color="secondary"
                    style={{ padding: 7 }}
                    onClick={() => (newQuantity === 0 ? 0 : setNewQuantity(newQuantity - 1))}
                    className={classes.iconButtons}>
                    <Remove />
                  </IconButton>
                </Grid>
                <Grid item xs={4} container justifyContent="center" alignItems="center">
                  <Typography
                    style={{
                      fontWeight: "bold",
                      color: "black",
                      fontFamily: titleStyles?.textFontFamily ?? "Inter",
                    }}>
                    {newQuantity}
                  </Typography>
                </Grid>
                <Grid item xs={4} container justifyContent="center">
                  <IconButton
                    color="secondary"
                    style={{ padding: 7 }}
                    onClick={() => increaseQuantity()}
                    className={classes.iconButtons}>
                    <Add />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
        <Grid
          item
          style={{ position: "relative" }}
          xs={item?.maxAmountPerInstance === 1 ? 4 : 7}
          container
          justifyContent="center">
          <Grid item xs={10}>
            <Tooltip
              title={
                !verifySubitems.passes
                  ? `${t("youreMissing")} ${verifySubitems.missing
                      .map((s, i) => (i === verifySubitems.missing.length - 1 ? s + "." : s))
                      .join(", ")}`
                  : unavailable?.closed
                  ? t("wereClosedItems")
                  : t("addProductToCart")
              }>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  disabled={disableAddButton}
                  onClick={submit}
                  color={disableAddButton ? "default" : "primary"}
                  id="add-modal"
                  variant="contained">
                  <Typography
                    style={{
                      textTransform: "none",
                      color: "inherit",
                      fontFamily: titleStyles?.textFontFamily ?? "Inter",
                      whiteSpace: "nowrap",
                    }}>
                    {t("add")} {currencyFormatter(actualPrice, currency)}
                  </Typography>
                </Button>
              </div>
            </Tooltip>
          </Grid>
          <Grid item xs={2} style={{ paddingLeft: 15 }}>
            <div className={classes.scrollArrowDiv}>
              <IconButton onClick={arrowScroll} style={{ padding: 4 }}>
                <KeyboardArrowDown className={classes.scrollArrow} id="scroll-arrow" />
              </IconButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
    ) : null;
  }, [
    actualPrice,
    classes.addbuttons,
    classes.cardBackground,
    classes.iconButtons,
    classes.scrollArrow,
    classes.scrollArrowDiv,
    cleverlynk?.id,
    currency,
    disableAddButton,
    item?.decorative,
    item?.maxAmountPerInstance,
    newQuantity,
    submit,
    titleStyles?.textFontFamily,
    unavailable?.closed,
    verifySubitems.missing,
    verifySubitems.passes,
  ]);

  const dialogHeight = () => {
    if (isMobile) {
      return "";
    } else {
      let h = upsellingItem && !upsellingItem.soldOut ? 170 : 0;
      if (cardType === PFXCardType.VERTICAL) {
        h = h + Math.min(window.innerHeight - 100, (imageWidth ?? 400) * 1.5);
      } else {
        h = h + (imageWidth ?? 400);
      }
      return h;
    }
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      fullWidth
      style={{ zIndex: 2000 }}
      maxWidth="md"
      transitionDuration={{
        enter: 300,
        exit: 200,
      }}
      open={open}
      onClose={() => {
        if (item?.id) handleAddRecentlyViewed(item.id);
        setClose();
      }}
      classes={{
        paperFullWidth: classes.dialogFullWidth,
      }}
      aria-labelledby="item-dialog-title">
      <DialogContent
        id="item-card-dialog-content"
        onScroll={() => {
          if (isMobile) {
            toggleScrollArrow(isMobile, imageWidth);
          }
        }}
        style={{
          height: dialogHeight(),
        }}
        classes={{
          root: classes.dialogContent,
        }}>
        <Grid id={`scroller-div-item`} container style={{ height: "inherit", overflow: "hidden" }}>
          {isMobile && (
            <IconButton
              color="secondary"
              onClick={() => {
                if (item?.id) handleAddRecentlyViewed(item.id);
                setClose();
              }}
              size="medium"
              className={classes.cancelIcon}>
              {" "}
              <Cancel />{" "}
            </IconButton>
          )}
          <Grid item xs={12} md={cardType === PFXCardType.VERTICAL ? 6 : 7} style={{ position: "relative" }}>
            <Grid
              container
              justifyContent={isMobile ? "flex-start" : "flex-end"}
              style={{
                position: isMobile ? "fixed" : "absolute",
                top: 5,
                right: isMobile ? "" : 5,
                left: isMobile ? 5 : "",
                zIndex: 998,
              }}>
              {item?.attachment && (
                <IconButton
                  target="_blank"
                  href={item?.attachment}
                  // style={{ zIndex: 9999 }}
                  classes={{ root: classes.linkButtonRoot }}
                  color="primary">
                  <Link />
                </IconButton>
              )}
            </Grid>
            <div {...swipeable} style={{ height: "100%" }}>
              <div
                className={`vertical ${classes.media}`}
                ref={(elem: any) => (elem && !imageWidth ? setImageWidth(elem.scrollWidth) : {})}>
                {imageWidth && image}
                {!noImage && (
                  <CircularProgress
                    color="secondary"
                    style={{
                      position: "absolute",
                      top: "46%",
                      left: "46%",
                    }}
                  />
                )}
                {item && (item.images ?? []).filter(i => i !== "").length > 1 && (
                  <React.Fragment>
                    <CarouselArrow
                      onClick={new_index => photoNavClick(new_index + LEFT, LEFT)}
                      focusIndex={focusIndex}
                      direction={LEFT}
                    />
                    <CarouselDots
                      focusColor={theme.palette.secondary.main}
                      focusIndex={focusIndex}
                      images={item?.images ?? []}
                      onClick={e => setFocusIndex(e)}
                      upselling={!!(upsellingItem && !upsellingItem.soldOut && !isMobile)}
                    />
                    <CarouselArrow
                      onClick={new_index => photoNavClick(new_index + RIGHT, RIGHT)}
                      focusIndex={focusIndex}
                      direction={RIGHT}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>
            {upsellingItem && !upsellingItem.soldOut && !isMobile && <UpsellingSection item={upsellingItem} desktop />}
          </Grid>
          <Grid
            item
            container
            alignItems={shouldCenter ? "center" : "baseline"}
            xs={12}
            md={cardType === PFXCardType.VERTICAL ? 6 : 5}
            direction="row"
            wrap="wrap"
            className={`${classes.cardBackground} ${classes.detail}`}>
            {!isMobile && (
              <IconButton
                color="secondary"
                onClick={() => {
                  if (item?.id) handleAddRecentlyViewed(item.id);
                  setClose();
                }}
                size="medium"
                className={classes.cancelIcon}>
                {" "}
                <Cancel />{" "}
              </IconButton>
            )}
            <Grid
              item
              container
              xs={12}
              className={classes.info}
              id="item-card-info-area"
              onScroll={() => {
                if (!isMobile) {
                  toggleScrollArrow(isMobile, imageWidth);
                }
              }}>
              {/* {item?.deliveryDelay && shouldCenter &&(<DeliveryDelay xs={12} sm={12} deliveryDelay={item!.deliveryDelay} />)} */}
              <Grid item xs={12} className={classes.priceName}>
                <Typography
                  variant="h5"
                  style={{ textAlign: shouldCenter ? "center" : "left" }}
                  className={`title ${classes.text}`}>
                  {item?.name}
                </Typography>
                {!item?.decorative && item?.price !== 0 && (
                  <Typography
                    variant="overline"
                    component="p"
                    className={classes.text}
                    style={{ fontSize: "1.1rem", textAlign: shouldCenter ? "center" : "left" }}>
                    {currencyFormatter(item?.priceWithDiscount ?? 0, currency)}
                  </Typography>
                )}
                {item?.deliveryDelay ? (
                  <Typography
                    style={{ whiteSpace: "break-spaces", marginBottom: "14px" }}
                    align={shouldCenter ? "center" : "justify"}
                    className={`description ${classes.text}`}
                    variant="caption"
                    component="p">
                    {t("productDeliversIn")}{" "}
                    {item?.deliveryDelay / 24 > 1
                      ? `${item?.deliveryDelay / 24} días.`
                      : `${item?.deliveryDelay / 24} día.`}
                  </Typography>
                ) : null}
              </Grid>

              <Grid
                item
                xs={12}
                style={{
                  height:
                    item?.subitems && item?.subitems.length > 0
                      ? "max-content"
                      : !item?.description || item?.description === "" || item?.description.split("").length < 150
                      ? ""
                      : "80%",
                }}>
                <Box
                  style={{
                    height: "100%",
                    padding: isMobile ? "0px 10px 10px" : "0px 0px 10px 0px",
                    width: !isMobile && shouldCenter ? "90%" : "",
                    margin: !isMobile && shouldCenter ? "0 auto" : "",
                  }}>
                  <Typography
                    style={{ whiteSpace: "break-spaces" }}
                    align={shouldCenter ? "center" : "justify"}
                    className={`description ${classes.text}`}
                    variant="body1">
                    {item?.description}
                  </Typography>
                </Box>
              </Grid>
              {!item?.decorative &&
                item?.subitems &&
                item?.subitems.filter(s => !s.hidden).map(subitem => renderSubitem(subitem))}
              {upsellingItem && !upsellingItem.soldOut && isMobile && <UpsellingSection item={upsellingItem} />}
            </Grid>
            {additionButtons}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ItemCardModal;
